// Wraps react-markdown with safe defaults. We do NOT enable rehype-raw
// (no inline HTML) to keep the surface small.
//
// Sprint code-block-render (2026-05-12): fenced code blocks are rendered
// through `<CodeBlock />` (header + copy button + framed body). Inline
// code uses a semantic `text-mono` + `bg-card` chip. Syntax highlighting
// is intentionally out of scope.
//
// Sprint md-doc-viewer (ADR D-012) — opt-in extensions:
//   - onDocPathClick: when set, plain text inside text-bearing components
//     (p / li / heading / blockquote / td) is scanned for `.md` paths and
//     each match is replaced with a <DocPathChip />. Fenced code blocks are
//     NOT processed (a fenced block is a literal code sample). Default
//     behaviour (no chips) when the prop is absent.
//   - extraComponents: lets a caller override individual components without
//     re-implementing the typography map. The DocViewerSheet uses this to
//     intercept `a` for internal `[X](other.md)` links.
//
// Sprint md-viewer-coverage-gaps (2026-05-13) — D-001: inline backtick code
// whose ENTIRE content matches a single `.md` path renders as a clickable
// chip instead of a monospace `<code>`. Agents instinctively format paths
// with backticks; pre-fix the chip was almost never visible. Mixed text
// inside the same inline code falls back to plain `<code>` to keep
// false-positive risk at zero. Fenced blocks (` ``` `) remain unchanged
// per D-002.

import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { detectMdPaths } from '../lib/detectMdPaths';
import { CodeBlock } from './ai-elements/code-block';
import { DocPathChip } from './DocPathChip';

function extractLanguage(className: string | undefined): string | null {
  if (!className) return null;
  const match = className.match(/language-([\w-]+)/);
  return match ? match[1]! : null;
}

function flattenChildrenToString(children: ReactNode): string {
  if (children === null || children === undefined) return '';
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(flattenChildrenToString).join('');
  return '';
}

function processChildren(
  children: ReactNode,
  onDocPathClick?: (path: string) => void,
): ReactNode {
  if (!onDocPathClick) return children;
  if (typeof children === 'string') return interleaveChips(children, onDocPathClick);
  if (Array.isArray(children)) {
    return children.map((c, i) =>
      typeof c === 'string' ? (
        <ChipInterleaver key={i} text={c} onClick={onDocPathClick} />
      ) : (
        c
      ),
    );
  }
  return children;
}

function ChipInterleaver({
  text,
  onClick,
}: {
  text: string;
  onClick: (p: string) => void;
}) {
  return <>{interleaveChips(text, onClick)}</>;
}

function interleaveChips(
  text: string,
  onClick: (p: string) => void,
): ReactNode {
  const matches = detectMdPaths(text);
  if (matches.length === 0) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    parts.push(
      <DocPathChip
        key={`${m.start}-${m.path}-${i}`}
        path={m.path}
        onOpen={onClick}
      />,
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function buildComponents(onDocPathClick?: (path: string) => void): Components {
  return {
    a: ({ children, href, ...rest }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-action hover:text-action/80 underline underline-offset-2"
        {...rest}
      >
        {children}
      </a>
    ),
    code: ({ className, children }) => {
      const text = flattenChildrenToString(children);
      const language = extractLanguage(className);
      const isBlock = text.includes('\n') || language !== null;
      if (isBlock) {
        // Strip the single trailing newline that react-markdown / remark
        // inject after the closing fence — keeps the copied payload clean.
        const trimmed = text.endsWith('\n') ? text.slice(0, -1) : text;
        return <CodeBlock code={trimmed} language={language} />;
      }
      if (onDocPathClick) {
        const matches = detectMdPaths(text);
        if (
          matches.length === 1 &&
          matches[0]!.start === 0 &&
          matches[0]!.end === text.length
        ) {
          return <DocPathChip path={matches[0]!.path} onOpen={onDocPathClick} />;
        }
      }
      return (
        // break-all: un token largo de código inline (una ruta, un id) no
        // tiene espacios donde partir — sin esto se salía del contenedor en
        // pantallas estrechas (chat del COO en móvil, 8px cortados).
        <code className="bg-card text-foreground rounded px-1 py-0.5 text-mono break-all">
          {children}
        </code>
      );
    },
    // The `pre` handler returns its children unchanged: when the child is
    // a block `code`, we already rendered a `<CodeBlock />` (which owns
    // its own `<pre>`), so wrapping it in another `<pre>` would double-
    // nest. When the child is anything else (rare edge), we still emit
    // the children verbatim.
    pre: ({ children }) => <>{children}</>,
    ul: ({ children, ...rest }) => (
      <ul className="list-disc pl-5 my-2 space-y-1" {...rest}>
        {children}
      </ul>
    ),
    ol: ({ children, ...rest }) => (
      <ol className="list-decimal pl-5 my-2 space-y-1" {...rest}>
        {children}
      </ol>
    ),
    li: ({ children, ...rest }) => (
      <li className="leading-relaxed" {...rest}>
        {processChildren(children, onDocPathClick)}
      </li>
    ),
    p: ({ children, ...rest }) => (
      <p className="my-2 leading-relaxed first:mt-0 last:mb-0" {...rest}>
        {processChildren(children, onDocPathClick)}
      </p>
    ),
    h1: ({ children, ...rest }) => (
      <h1 className="text-display mt-3 mb-1" {...rest}>
        {processChildren(children, onDocPathClick)}
      </h1>
    ),
    h2: ({ children, ...rest }) => (
      <h2 className="text-display font-semibold mt-3 mb-1" {...rest}>
        {processChildren(children, onDocPathClick)}
      </h2>
    ),
    h3: ({ children, ...rest }) => (
      <h3 className="text-body-chat font-semibold mt-2 mb-1" {...rest}>
        {processChildren(children, onDocPathClick)}
      </h3>
    ),
    blockquote: ({ children, ...rest }) => (
      <blockquote
        className="border-l-2 border-border pl-3 italic text-muted-foreground my-2"
        {...rest}
      >
        {processChildren(children, onDocPathClick)}
      </blockquote>
    ),
    table: ({ children, ...rest }) => (
      <div className="overflow-x-auto my-2">
        <table className="min-w-full text-body border border-border" {...rest}>
          {children}
        </table>
      </div>
    ),
    th: ({ children, ...rest }) => (
      <th
        className="border border-border bg-card px-2 py-1 text-left font-semibold"
        {...rest}
      >
        {processChildren(children, onDocPathClick)}
      </th>
    ),
    td: ({ children, ...rest }) => (
      <td className="border border-border px-2 py-1" {...rest}>
        {processChildren(children, onDocPathClick)}
      </td>
    ),
  };
}

export interface MarkdownRendererProps {
  text: string;
  /**
   * Sprint md-doc-viewer — when set, `.md` paths in plain text are turned
   * into clickable chips. Default (undefined) preserves the original
   * Sprint 4 behaviour.
   */
  onDocPathClick?: (path: string) => void;
  /**
   * Sprint md-doc-viewer — overrides for specific components (e.g. the
   * DocViewerSheet replaces `a` to keep internal `[X](other.md)` links
   * inside the overlay). Spread on top of the default map.
   */
  extraComponents?: Partial<Components>;
}

export function MarkdownRenderer({
  text,
  onDocPathClick,
  extraComponents,
}: MarkdownRendererProps) {
  const base = buildComponents(onDocPathClick);
  const components: Components = extraComponents
    ? { ...base, ...extraComponents }
    : base;
  return (
    <div className="text-body-chat text-foreground" data-testid="markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
